import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  GeoLocationType,
  IGeoPlacesQueryParams,
} from "~/entity/Geo/model/shema";
import { useUrlFilters } from "~/features/filter";

import { addressSelectionGuideFilterFields } from "./fields";

type AddressSelectionGuideFilterValues = Pick<
  IGeoPlacesQueryParams,
  | "id"
  | "parentId"
  | "name"
  | "type"
  | "socr"
  | "index"
  | "cladrCode"
  | "okatoCode"
  | "fiasId"
  | "fiasValue"
  | "active"
>;

const toNumberOrUndefined = (raw: unknown): number | undefined => {
  if (raw == null || raw === "") return undefined;
  const num = Number(raw);
  return Number.isFinite(num) ? num : undefined;
};

const toStringOrUndefined = (raw: unknown): string | undefined => {
  if (raw == null || raw === "") return undefined;
  return String(raw);
};

const toBooleanOrUndefined = (raw: unknown): boolean | undefined => {
  if (raw === "true") return true;
  if (raw === "false") return false;
  return undefined;
};

/**
 * Возвращает значения фильтра справочника адресов из URL,
 * приведённые к типам параметров GET /geo/places.
 */
export const useAddressSelectionGuideFilterValues =
  (): AddressSelectionGuideFilterValues => {
    const { t } = useTranslation();
    const fields = useMemo(() => addressSelectionGuideFilterFields(t), [t]);
    const { values } = useUrlFilters(fields);

    return useMemo<AddressSelectionGuideFilterValues>(
      () => ({
        id: toNumberOrUndefined(values.id),
        parentId: toNumberOrUndefined(values.parentId),
        name: toStringOrUndefined(values.name),
        type: toStringOrUndefined(values.type) as GeoLocationType | undefined,
        socr: toStringOrUndefined(values.socr),
        index: toStringOrUndefined(values.index),
        cladrCode: toStringOrUndefined(values.cladrCode),
        okatoCode: toStringOrUndefined(values.okatoCode),
        fiasId: toStringOrUndefined(values.fiasId),
        fiasValue: toStringOrUndefined(values.fiasValue),
        active: toBooleanOrUndefined(values.active),
      }),
      [values],
    );
  };
